import ApexCharts from "apexcharts";
import type { ApexOptions } from "apexcharts";

export interface GaugeProps {
  readonly value: number; // 0-100
  readonly label: string;
  readonly size?: number; // px
  readonly showName?: boolean; // ゲージ内にラベル名を表示するか（小型ゲージは false 推奨）。既定 true
  readonly ctor?: typeof ApexCharts; // テスト注入用
}

// ApexCharts radialBar(Salesforce 風ゲージ)の薄いラッパー。
// 視覚に依存しないよう role=img + aria-label + sr-only テキストで値を併記する(WCAG2)。
export function createGauge(props: GaugeProps): HTMLElement {
  const size = props.size ?? 180;
  const wrap = document.createElement("div");
  wrap.className = "relative inline-flex flex-col items-center";
  wrap.setAttribute("role", "img");
  wrap.setAttribute("aria-label", props.label + " " + props.value + "%");

  const mount = document.createElement("div");
  mount.style.width = size + "px";
  mount.style.height = size + "px";

  const text = document.createElement("span");
  text.className = "sr-only";
  text.dataset["testid"] = "gauge-value";
  text.textContent = props.value + "%";

  wrap.append(mount, text);

  const Ctor = props.ctor ?? ApexCharts;
  const options: ApexOptions = {
    chart: {
      type: "radialBar",
      height: size,
      width: size,
      animations: { enabled: false },
      sparkline: { enabled: true },
    },
    series: [props.value],
    labels: [props.label],
    colors: ["#a34a00"], // shinonome-700
    plotOptions: {
      radialBar: {
        hollow: { size: "62%" },
        track: { background: "#f1f5f9" }, // slate-100
        dataLabels: {
          name: { show: props.showName !== false, fontSize: "12px", color: "#475569" },
          value: {
            show: true,
            offsetY: props.showName === false ? 6 : 0,
            fontSize: "24px",
            color: "#0f172a",
            formatter: (v: number) => Math.round(v) + "%",
          },
        },
      },
    },
  };
  // ApexCharts は描画時にコンテナが DOM 接続済みであることを要求する（detached だと無描画で
  // 早期 return する）。create* は要素を返すだけで呼び出し側が後から append するため、
  // 同一タスク内の append 完了後に描画する。requestAnimationFrame は背面/非表示タブで発火
  // しない（背面タブでゲージが描画されない）ため、タブ可視性に依存しない queueMicrotask を使う。
  const renderChart = (): void => {
    try {
      const chart = new Ctor(mount, options);
      void chart.render();
    } catch (e) {
      console.error("[gauge] render failed", e);
    }
  };
  if (mount.isConnected) {
    renderChart();
  } else {
    queueMicrotask(renderChart);
  }
  return wrap;
}
